// Activity stream event shape — the cross-module messaging substrate.
// See DOCS/architecture/activity-stream.md.
//
// Per-kind payload schemas live next to their owning module
// (packages/contracts/<area>/events.ts). A subscriber that handles
// multiple kinds discriminates on `kind` and casts payload accordingly.
import { z } from 'zod';
// ---------------------------------------------------------------------------
// ActorKind — who performed the action.
// ---------------------------------------------------------------------------
export const ActorKind = z.enum(['user', 'system', 'job']);
// ---------------------------------------------------------------------------
// ActivityEvent — the wire shape, persisted in `activities` and broadcast on
// the Redis pub/sub channel `activities`.
// ---------------------------------------------------------------------------
export const ActivityEvent = z.object({
    id: z.string().uuid(),
    /** Stable kind string, e.g. 'article.published', 'comment.created'. */
    kind: z.string().min(1),
    /** Per-kind payload schema version. Subscribers may pin via `versions`. */
    schemaVersion: z.number().int().min(1),
    actorKind: ActorKind,
    /** UserId for `user`; `system` for cron / boot; jobId for queue-driven. */
    actorId: z.string().min(1),
    /** Polymorphic subject — matches a LinkableKind kind. */
    subjectKind: z.string().min(1),
    subjectId: z.string().min(1),
    /** Visibility — drives `canRead` filters at downstream subscribers
     *  (notifications recipient resolution, search reindex scoping). */
    groupIds: z.array(z.string()),
    /** Kind-specific payload. Schema lives in the owning module's events.ts. */
    payload: z.record(z.string(), z.unknown()),
    /** ISO 8601 UTC. Always assigned by the publisher; never client-supplied. */
    createdAt: z.string().datetime({ offset: true }),
});
// ---------------------------------------------------------------------------
// Activity reserved kind constants — referenced by core subscribers.
// ---------------------------------------------------------------------------
/** Kinds emitted by the activity module itself (meta-events). */
export const ACTIVITY_META_KINDS = {
    SubscriberLag: 'activity.subscriber-lag',
    SubscriberFailedBatch: 'activity.subscriber-failed-batch',
    ArchiveRotated: 'activity.archive-rotated',
};
/**
 * Pattern used by subscribers that match families of kinds.
 * The subscriber framework expands these at registration time.
 */
export const ACTIVITY_KIND_PATTERNS = {
    Wildcard: '*',
    AnyCreated: '*.created',
    AnyUpdated: '*.updated',
    AnyDeleted: '*.deleted',
    AnyPublished: '*.published',
};
//# sourceMappingURL=event.js.map